// 라우트 경로 단일 정의(FR-COM-01). 각 모듈은 독립 경로로 접근된다.
// /v = 메타버스 입장(W-09), /world = 월드 본체. /book·/ar 은 코드스플릿 대상(FR-COM-09).
export const ROUTES = {
  landing: "/",
  book: "/book",
  ar: "/ar",
  verse: "/v", // 메타버스 입장 화면(히어로 로드/즉석 생성)
  world: "/world", // 월드(존 탐험)
  hero: "/h", // 익명 히어로 코드 복원(웹 동일 출처, M2) — 자리만
  rally: "/rally", // 경주 스탬프 랠리(위치기반)
  guide: "/guide", // 보호자 안내(글은 SiteText — 관리자에서 고친다)
  familyVoice: "/family/voice", // 🎙️ 보호자 녹음(어른 확인 뒤 · 이 기기에만 · 리서치 최종안 ⑦)
  family: "/family", // 👪 보호자 화면 — 어른 확인 뒤 굿즈·행사·소식(영유아 모드, 조치계획서 ■1)
  privacy: "/privacy", // 🔒 개인정보처리방침 — 기관·학교가 가리킬 수 있는 주소 하나(조치계획서 ■10)
  licenses: "/licenses", // 📜 오픈소스·자료 고지 — 목록은 빌드가 package.json 에서 읽는다(조치계획서 ■7)
  familyBook: "/mybook", // 👪 부모가 QR 로 여는 그림책 한 권(읽기만 — 아무것도 안 되살린다)
  community: "/community", // 우리 마을 이야기(운영자 소식·집계) — UGC 없음(무수집·아동안전)
  goods: "/goods", // 굿즈 상세 공유 링크(/goods/:id) — QR·SNS 유입(docs/build/16 §1-1)
  goodsPreview: "/goods-preview", // 관리자 미리보기 수신(iframe·postMessage 전용, 사용자 동선 아님)
} as const;

/** 굿즈 상세 공유 URL(QR·SNS). id 는 shared 의 안전 id 규칙([a-z0-9_])이라 인코딩 불필요. */
export function goodsPath(id: string): string {
  return `${ROUTES.goods}/${id}`;
}

export type RouteKey = keyof typeof ROUTES;

/**
 * 이 주소가 **랠리**인가 — 지도(`content`)가 `/rally` 를 가리키면 체험공간에서는
 * 주소를 옮기지 않고 **그 자리에서** 팝업으로 연다.
 *
 * 지도는 비개발자가 고친다(CLAUDE.md) — 그래서 오브젝트에 새 속성을 요구하지 않고
 * **이미 적혀 있는 주소로 알아본다.** 쿼리(`?p=&t=`)가 붙어도 같은 곳이다.
 */
export function isRallyUrl(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split(/[?#]/)[0]!.replace(/\/$/, "");
  return path === ROUTES.rally;
}

/**
 * 🚪 **체험공간에서 무언가를 눌렀을 때 어디로 가는가.**
 *
 * 셋을 가른다. 하나만 어긋나도 아이가 겪는 일이 완전히 달라진다.
 *
 * | 갈래 | 무엇을 하나 | 어긋나면 |
 * |---|---|---|
 * | `rally` | **주소를 안 옮기고** 그 자리에서 연다 | 체험공간을 벗어난다 — 아이가 돌아오는 길을 모른다 |
 * | `internal` | 우리 화면으로 이동 | 바깥 고지 창이 뜬다(우리 화면인데) |
 * | `external` | **고지 창을 거쳐** 바깥으로 | 고지 없이 바깥으로 나간다(FR-COM-04 위반) |
 *
 * 🔴 `javascript:` 같은 활성 스킴은 `blocked` 다. 지도는 운영자가 게시하는 것이라
 * 외부 입력으로 본다(불변식 7) — 위쪽에서 이미 걸러도 **여기서 한 번 더** 막는다.
 * 「어차피 안 들어온다」로 두면, 거르는 곳이 하나 바뀌는 날 조용히 뚫린다.
 */
export type LinkTarget = "rally" | "internal" | "external" | "blocked";

export function linkTargetOf(url: string | undefined): LinkTarget {
  if (!url) return "blocked";
  const u = url.trim();
  if (isRallyUrl(u)) return "rally";
  // `//evil.com` 은 상대경로처럼 보이지만 **바깥**이다 — 내부로 보내면 안 된다
  if (u.startsWith("/") && !u.startsWith("//")) return "internal";
  return /^https?:\/\//i.test(u) ? "external" : "blocked";
}
